<template>
  <v-container fluid>
    <v-row align="center" justify="space-between" class="mb-4">
      <v-col cols="12" md="8" class="d-flex align-center">
        <v-btn text color="primary" class="mr-3" :to="{ name: 'empresa.ventas.pedidos-online.listado' }">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
        <div>
          <h2 class="text-h5 font-weight-bold">
            Pedido online {{ pedido ? pedido.numero_display : '' }}
          </h2>
        </div>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-chip v-if="pedido" :color="estadoColor(pedido.estado)" dark class="mr-2">
          {{ estadoLabel(pedido.estado) }}
        </v-chip>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col v-for="i in 3" :key="i" cols="12">
        <v-skeleton-loader type="list-item-three-line" />
      </v-col>
    </v-row>

    <template v-else-if="pedido">
      <v-row>
        <!-- Panel izquierdo -->
        <v-col cols="12" md="4">
          <v-card class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">Comprador</v-card-title>
            <v-card-text>
              <div class="font-weight-medium mb-2">{{ pedido.cliente_nombre || '—' }}</div>
              <div v-if="pedido.cliente_email" class="d-flex align-center mb-1">
                <v-icon small class="mr-2" color="grey">mdi-email-outline</v-icon>
                <a :href="`mailto:${pedido.cliente_email}`" class="caption">{{ pedido.cliente_email }}</a>
              </div>
              <div v-if="pedido.cliente_telefono" class="d-flex align-center">
                <v-icon small class="mr-2" color="grey">mdi-phone-outline</v-icon>
                <span class="caption">{{ pedido.cliente_telefono }}</span>
              </div>
              <div v-if="!pedido.cliente_email && !pedido.cliente_telefono" class="caption grey--text">
                Sin datos de contacto cargados
              </div>
            </v-card-text>
          </v-card>

          <v-card class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">Datos del pedido</v-card-title>
            <v-card-text>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Sucursal</span>
                <span class="caption">{{ pedido.sucursal_nombre }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Fecha</span>
                <span class="caption">{{ pedido.fecha_alta }}</span>
              </div>
              <div v-if="pedido.observaciones" class="mt-2">
                <span class="grey--text caption">Observaciones del comprador</span>
                <p class="body-2 mt-1">{{ pedido.observaciones }}</p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Totales -->
          <v-card class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">Totales</v-card-title>
            <v-card-text>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Subtotal</span>
                <span class="caption">$ {{ pedido.subtotal }}</span>
              </div>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between">
                <span class="font-weight-bold">Total</span>
                <span class="font-weight-bold">$ {{ pedido.total }}</span>
              </div>
            </v-card-text>
          </v-card>

          <!-- Acciones -->
          <v-card>
            <v-card-title class="subtitle-1 font-weight-bold">Acciones</v-card-title>
            <v-card-text class="d-flex flex-column" style="gap: 8px">
              <v-btn
                v-if="pedido.estado === 'enviado'"
                color="success"
                :loading="loadingTransicion === 'aprobado'"
                @click="ejecutarTransicion('aprobado')"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Confirmar pedido
              </v-btn>

              <v-btn
                v-if="pedido.estado === 'enviado'"
                color="error"
                outlined
                @click="dialogRechazar = true"
              >
                <v-icon left>mdi-cancel</v-icon>
                Rechazar pedido
              </v-btn>

              <!-- Orden de preparación: mismo flujo de fulfillment que cualquier venta aprobada -->
              <template v-if="pedido.estado === 'aprobado'">
                <v-btn
                  v-if="pedido.orden_activa"
                  color="teal"
                  outlined
                  :to="{ name: 'empresa.ventas.orden.consultar', params: { id: pedido.orden_activa.id } }"
                >
                  <v-icon left>mdi-clipboard-check</v-icon>
                  Ver Orden ({{ pedido.orden_activa.numero_display }})
                </v-btn>
                <v-btn
                  v-else
                  color="teal"
                  :loading="loadingOrden"
                  @click="crearOrden"
                >
                  <v-icon left>mdi-clipboard-list</v-icon>
                  Preparar pedido
                </v-btn>
              </template>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Panel derecho: detalle -->
        <v-col cols="12" md="8">
          <v-card>
            <v-card-title class="subtitle-1 font-weight-bold">
              Productos pedidos
              <v-spacer />
              <span class="caption grey--text">{{ pedido.detalles.length }} ítem(s)</span>
            </v-card-title>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th class="text-right">Cantidad</th>
                    <th class="text-right">Precio unit.</th>
                    <th class="text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in pedido.detalles" :key="d.id">
                    <td>{{ d.descripcion }}</td>
                    <td class="text-right">{{ d.cantidad }}</td>
                    <td class="text-right">$ {{ d.precio_venta }}</td>
                    <td class="text-right">$ {{ d.subtotal }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <dialog-confirmation-close
      v-if="dialogRechazar"
      @close-dialog="dialogRechazar = false"
      @process-accept="ejecutarTransicion('cancelado')"
    >
      <template v-slot:titulo>¿Rechazar este pedido?</template>
      <template v-slot:message>El comprador va a ver el pedido como cancelado. Esta acción no se puede revertir.</template>
      <template v-slot:buttonOK>Rechazar pedido</template>
    </dialog-confirmation-close>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import DialogConfirmationClose from '@/components/dialogs/confirmation.close.vue';

export default {
  name: 'PedidoOnlineEmpresaDetalle',
  components: { DialogConfirmationClose },
  data() {
    return {
      pedido: null,
      loading: false,
      loadingTransicion: null,
      loadingOrden: false,
      dialogRechazar: false,
    };
  },
  computed: {
    pedidoId() {
      return this.$route.params.id;
    },
  },
  methods: {
    ...mapActions('presupuestoVenta', ['get_presupuesto', 'cambiar_estado_presupuesto']),
    ...mapActions('ordenPreparacionVenta', ['create_orden']),

    estadoColor(estado) {
      return { enviado: 'blue', aprobado: 'green', cancelado: 'red darken-1' }[estado] || 'grey';
    },

    estadoLabel(estado) {
      return { enviado: 'Pendiente de confirmar', aprobado: 'Confirmado', cancelado: 'Rechazado' }[estado] || estado;
    },

    async fetchPedido() {
      this.loading = true;
      try {
        const res = await this.get_presupuesto(this.pedidoId);
        this.pedido = res.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async ejecutarTransicion(estado) {
      this.dialogRechazar = false;
      this.loadingTransicion = estado;
      try {
        await this.cambiar_estado_presupuesto({ id: this.pedidoId, estado });
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'success',
          text: estado === 'aprobado' ? 'Pedido confirmado' : 'Pedido rechazado',
          icon: 'mdi-check', timeout: 2000,
        });
        await this.fetchPedido();
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al actualizar el pedido';
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'error',
          text: msg, icon: 'mdi-alert', timeout: 3500,
        });
      } finally {
        this.loadingTransicion = null;
      }
    },

    async crearOrden() {
      this.loadingOrden = true;
      try {
        const res = await this.create_orden({ presupuesto: this.pedidoId });
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'success',
          text: 'Orden de preparación creada', icon: 'mdi-check', timeout: 2500,
        });
        this.$router.push({ name: 'empresa.ventas.orden.consultar', params: { id: res.data.id } });
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al crear la orden';
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'error',
          text: msg, icon: 'mdi-alert', timeout: 3500,
        });
      } finally {
        this.loadingOrden = false;
      }
    },
  },
  created() {
    this.fetchPedido();
  },
};
</script>
